import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import { queryKeys } from "./queries";
import { rpc } from "./rpc";
import { compact, indexAbilities } from "./documents";
import type {
  Collection,
  Group,
  Membership,
  Permission,
  ResourceGroupMembership,
  User,
} from "./types";

/**
 * The collection surface (`docs/spec/collections.md` §4). `collections.list` is
 * already owned by `queries.ts` because the shell's sidebar needed it first;
 * everything a collection *screen* needs lives here.
 */

export const collectionQueryKeys = {
  info: (id: string) => ["collections.info", id] as const,
  memberships: (id: string) => ["collections.memberships", id] as const,
  groupMemberships: (id: string) => ["collections.group_memberships", id] as const,
};

export interface CollectionInfo {
  collection: Collection;
  abilities: Record<string, boolean>;
}

/** Accepts a UUID or a collection slug, exactly as the server's resolver does. */
export function useCollection(id: string | undefined): UseQueryResult<CollectionInfo> {
  return useQuery({
    queryKey: collectionQueryKeys.info(id ?? ""),
    enabled: id !== undefined && id !== "",
    retry: false,
    queryFn: async () => {
      const result = await rpc<Collection>("collections.info", { id });
      return {
        collection: result.data,
        abilities: indexAbilities(result.policies)[result.data.id] ?? {},
      };
    },
  });
}

export interface CollectionUpdateInput {
  id: string;
  name?: string;
  description?: string | null;
  data?: Record<string, unknown>;
  icon?: string | null;
  color?: string | null;
  permission?: "read" | "read_write" | null;
  sharing?: boolean;
  commenting?: boolean | null;
  sort?: { field: string; direction: string };
}

function invalidateCollections(id?: string): (client: ReturnType<typeof useQueryClient>) => void {
  return (client) => {
    void client.invalidateQueries({ queryKey: queryKeys.collections });
    if (id !== undefined) {
      void client.invalidateQueries({ queryKey: collectionQueryKeys.info(id) });
    }
  };
}

function makeCollectionMutation<TInput extends { id?: string }>(
  method: string,
): () => UseMutationResult<Collection, Error, TInput> {
  return function useMethod(): UseMutationResult<Collection, Error, TInput> {
    const client = useQueryClient();
    return useMutation({
      mutationFn: async (input: TInput) =>
        (await rpc<Collection>(method, compact(input as Record<string, unknown>))).data,
      onSuccess: (_data, input) => {
        invalidateCollections(input.id)(client);
        void client.invalidateQueries({ queryKey: ["documents"] });
        void client.invalidateQueries({ queryKey: ["collections.documents"] });
      },
    });
  };
}

export const useUpdateCollection =
  makeCollectionMutation<CollectionUpdateInput>("collections.update");
export const useArchiveCollection = makeCollectionMutation<{ id: string }>("collections.archive");
export const useRestoreCollection = makeCollectionMutation<{ id: string }>("collections.restore");
export const useDeleteCollection = makeCollectionMutation<{ id: string }>("collections.delete");
export const useDuplicateCollection = makeCollectionMutation<{ id: string; name?: string }>(
  "collections.duplicate",
);
export const useMoveCollection = makeCollectionMutation<{ id: string; index: string }>(
  "collections.move",
);

/* ------------------------------------------------------------ memberships */

export interface CollectionMembers {
  memberships: Membership[];
  users: User[];
}

export function useCollectionMembers(
  id: string | undefined,
  enabled = true,
): UseQueryResult<CollectionMembers> {
  return useQuery({
    queryKey: collectionQueryKeys.memberships(id ?? ""),
    enabled: enabled && id !== undefined && id !== "",
    retry: false,
    queryFn: async () => {
      const result = await rpc<CollectionMembers>("collections.memberships", { id, limit: 100 });
      return { memberships: result.data.memberships ?? [], users: result.data.users ?? [] };
    },
  });
}

export interface CollectionGroupMembers {
  groupMemberships: ResourceGroupMembership[];
  groups: Group[];
}

export function useCollectionGroupMembers(
  id: string | undefined,
  enabled = true,
): UseQueryResult<CollectionGroupMembers> {
  return useQuery({
    queryKey: collectionQueryKeys.groupMemberships(id ?? ""),
    enabled: enabled && id !== undefined && id !== "",
    retry: false,
    queryFn: async () => {
      const result = await rpc<CollectionGroupMembers>("collections.group_memberships", {
        id,
        limit: 100,
      });
      return {
        groupMemberships: result.data.groupMemberships ?? [],
        groups: result.data.groups ?? [],
      };
    },
  });
}

function makeMembershipMutation<TInput extends { id: string }>(
  method: string,
): () => UseMutationResult<unknown, Error, TInput> {
  return function useMethod(): UseMutationResult<unknown, Error, TInput> {
    const client = useQueryClient();
    return useMutation({
      mutationFn: async (input: TInput) => (await rpc(method, input)).data,
      onSuccess: (_data, input) => {
        void client.invalidateQueries({ queryKey: collectionQueryKeys.memberships(input.id) });
        void client.invalidateQueries({ queryKey: collectionQueryKeys.groupMemberships(input.id) });
      },
    });
  };
}

export const useCollectionAddUser = makeMembershipMutation<{
  id: string;
  userId: string;
  permission: Permission;
}>("collections.add_user");
export const useCollectionRemoveUser = makeMembershipMutation<{ id: string; userId: string }>(
  "collections.remove_user",
);
export const useCollectionAddGroup = makeMembershipMutation<{
  id: string;
  groupId: string;
  permission: Permission;
}>("collections.add_group");
export const useCollectionRemoveGroup = makeMembershipMutation<{ id: string; groupId: string }>(
  "collections.remove_group",
);
